import { useEffect } from "react";

type PageMeta = {
  title: string;
  description: string;
  keywords: string;
  path: string;
  ogType?: string;
  jsonLd?: Record<string, unknown>;
};

function upsertTag(selector: string, create: () => HTMLElement, attrs: Record<string, string>) {
  let el = document.head.querySelector(selector) as HTMLElement | null;
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => el?.setAttribute(k, v));
  return el;
}

export default function usePageMeta({ title, description, keywords, path, ogType = "website", jsonLd }: PageMeta) {
  const jsonLdStr = jsonLd ? JSON.stringify(jsonLd) : "";

  useEffect(() => {
    const origin = window.location.origin;
    const url = `${origin}${path}`;
    document.title = title;

    upsertTag('meta[name="description"]', () => {
      const m = document.createElement("meta");
      m.setAttribute("name", "description");
      return m;
    }, { content: description });

    upsertTag('meta[name="keywords"]', () => {
      const m = document.createElement("meta");
      m.setAttribute("name", "keywords");
      return m;
    }, { content: keywords });

    upsertTag('meta[name="robots"]', () => {
      const m = document.createElement("meta");
      m.setAttribute("name", "robots");
      return m;
    }, { content: "index,follow,max-image-preview:large" });

    upsertTag('link[rel="canonical"]', () => {
      const l = document.createElement("link");
      l.setAttribute("rel", "canonical");
      return l;
    }, { href: url });

    upsertTag('meta[property="og:title"]', () => document.createElement("meta"), {
      property: "og:title",
      content: title,
    });
    upsertTag('meta[property="og:description"]', () => document.createElement("meta"), {
      property: "og:description",
      content: description,
    });
    upsertTag('meta[property="og:type"]', () => document.createElement("meta"), {
      property: "og:type",
      content: ogType,
    });
    upsertTag('meta[property="og:url"]', () => document.createElement("meta"), {
      property: "og:url",
      content: url,
    });
    upsertTag('meta[property="og:site_name"]', () => document.createElement("meta"), {
      property: "og:site_name",
      content: "瓜田热评",
    });
    upsertTag('meta[name="twitter:card"]', () => document.createElement("meta"), {
      name: "twitter:card",
      content: "summary_large_image",
    });
    upsertTag('meta[name="twitter:title"]', () => document.createElement("meta"), {
      name: "twitter:title",
      content: title,
    });
    upsertTag('meta[name="twitter:description"]', () => document.createElement("meta"), {
      name: "twitter:description",
      content: description,
    });

    let script: HTMLScriptElement | null = null;
    if (jsonLdStr) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-page-jsonld", "true");
      script.textContent = jsonLdStr;
      document.head.appendChild(script);
    }

    window.scrollTo(0, 0);

    return () => {
      if (script && script.parentNode) script.parentNode.removeChild(script);
    };
  }, [title, description, keywords, path, ogType, jsonLdStr]);
}